import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import axiosRetry from 'axios-retry';

export const emsSessionIdHeaderKey: string = 'x-ems-sessionid';
const includedHostnames: string[] = [
  'support.microsoft.com',
  'support-uat.microsoft.com',
  'support.office.com',
  'emerald-ppe.azurewebsites.net',
  'emerald-prod.azurewebsites.net',
  'emerald-ppe.microsoft.com',
  'emerald.microsoft.com',
];

interface IEnhancedConfig {
  axiosRequestConfig: AxiosRequestConfig
  cancelTokenId: string
}

interface ICancelablePromise<T> extends Promise<T> {
  cancelTokenId: string
}

class HttpUtils {
  private client: AxiosInstance;
  private cancellationTokenMap: Map<string, CancelTokenSource>;
  private emsSessionId: string;
  private __RequestVerificationToken: string;

  public constructor() {
    const timeout = 60000;
    this.__RequestVerificationToken = (document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement)?.value || '';
    let config: AxiosRequestConfig = { timeout: timeout };

    this.client = axios.create(config);
    this.cancellationTokenMap = new Map<string, CancelTokenSource>();
    this.emsSessionId = '';

    this.client.interceptors.request.use(
      (innerConfig: any) => {
        innerConfig = this._requestInterceptor(innerConfig)
        return innerConfig;
      },
      (error: Error) => {
        console.log(`Axios request reject at reason: ${error}`);
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        response = this._responseInterceptor(response);
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.createResponseInterceptors();
  }

  public get<T>(
    url: string,
    config?: AxiosRequestConfig,
    customHeader?: {},
    getStatus?: boolean,
    retries?: number
  ): ICancelablePromise<T> {
    const { axiosRequestConfig, cancelTokenId } = this.enhanceRequestConfig(config, customHeader);

    const localClient = retries ? this.getRetryClientCopy(retries) : this.client;
    const axiosPromise = localClient
      .get(url, axiosRequestConfig)
      .then((response: AxiosResponse<T>): any => {
        return this.onSuccessWithCancelToken(cancelTokenId, response, getStatus);
      })
      .catch((reason: AxiosError) => {
        return this.onReject(url, reason, cancelTokenId);
      });

    return this.extendPromiseToBeCancelable(axiosPromise, cancelTokenId);
  }

  public post<T>(
    url: string,
    data: Object,
    customHeader?: {},
    config?: AxiosRequestConfig,
    getStatus?: boolean,
    retries?: number
  ): ICancelablePromise<T> {
    const { axiosRequestConfig, cancelTokenId } = this.enhanceRequestConfig(config, customHeader);

    const localClient = retries ? this.getRetryClientCopy(retries) : this.client;

    const axiosPromise: Promise<T> = localClient
      .post(url, data, axiosRequestConfig)
      .then((response: AxiosResponse<T>): any => {
        return this.onSuccessWithCancelToken(cancelTokenId, response, getStatus);
      })
      .catch((reason: AxiosError) => {
        return this.onReject(url, reason, cancelTokenId);
      });

    return this.extendPromiseToBeCancelable(axiosPromise, cancelTokenId);
  }

  public patch<T>(
    url: string,
    data: T,
    customHeader?: {},
    config?: AxiosRequestConfig,
    retries?: number
  ): Promise<T> {
    const { axiosRequestConfig } = this.enhanceRequestConfig(config, customHeader);
    const localClient = retries ? this.getRetryClientCopy(retries) : this.client;
    return localClient
      .patch(url, data, axiosRequestConfig)
      .then(this.onSuccess)
      .catch(this.onReject.bind(this, url));
  }

  public put<T>(
    url: string,
    data: Object,
    retries?: number
  ): Promise<T> {
    const localClient = retries ? this.getRetryClientCopy(retries) : this.client;
    return localClient
      .put(url, data)
      .then(this.onSuccess)
      .catch(this.onReject.bind(this, url));
  }

  public delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    retries?: number
  ): Promise<T> {
    const localClient = retries ? this.getRetryClientCopy(retries) : this.client;
    return localClient
      .delete(url, config)
      .then(this.onSuccess)
      .catch(this.onReject.bind(this, url));
  }

  public cancelRequest(cancelTokenId: string, message?: string): Promise<void> {
    return Promise.resolve(this.getTokenFromMapAndCancel(cancelTokenId, message));
  }

  public cancelAllRequests(cancelTokenIds: string[], genericMessage?: string): Promise<void[]> {
    return Promise.all(
      cancelTokenIds.map((cts: string) => {
        return this.cancelRequest(cts, genericMessage);
      })
    );
  }

  public setEmsSessionId(emsSessionId: string) {
    if(!this.emsSessionId) {
      this.emsSessionId = emsSessionId;
    }
  }

  public getEmsSessionId() {
    return this.emsSessionId;
  }

  private onSuccessWithCancelToken<T>(
    cancelTokenId: string,
    response: AxiosResponse<T>,
    getStatus?: boolean
  ): Promise<{ status: number; data: T }> | Promise<T> {
    this.cancellationTokenMap.delete(cancelTokenId);
    const data = this.onSuccess(response);
    return getStatus ? Promise.resolve({ status: response.status, data: response.data }) : Promise.resolve(data);
  }

  private onSuccess<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private onReject(url: string, error: AxiosError, cancelTokenId?: string): Promise<never> {
    console.log(`Reject on then: ${url}: reason: ${error.message}`);
    // if cancelTokenId is defined, remove it now that the request is complete
    if (cancelTokenId) {
      this.cancellationTokenMap.delete(cancelTokenId);
    }

    // If authorization token is there,
    // remove it from error so it does not get logged in down flow
    if (!!error && !!error.config && !!error.config.headers && !!error.config.headers.Authorization) {
      delete error.config.headers.Authorization;
    }

    return Promise.reject(error);
  }

  private getTokenFromMapAndCancel(cancelTokenId: string, message?: string): void {
    if (this.cancellationTokenMap.has(cancelTokenId) === true) {
      (this.cancellationTokenMap.get(cancelTokenId) as CancelTokenSource).cancel(message);
    }
  }

  private extendPromiseToBeCancelable<T>(axiosPromise: Promise<T>, cancelTokenId: string): ICancelablePromise<T> {
    const cancelableAxiosPromise: ICancelablePromise<T> = axiosPromise as ICancelablePromise<T>;
    cancelableAxiosPromise.cancelTokenId = cancelTokenId;
    return cancelableAxiosPromise;
  }

  private enhanceRequestConfig(config?: AxiosRequestConfig, customHeader?: {}): IEnhancedConfig {
    config = config || {};

    if (customHeader) {
      config.headers = customHeader;
    } else {
      config.headers = { 'Content-Type': 'application/json' };
    }
    config.headers = { ...config.headers, 'X-CSRF-TOKEN': this.__RequestVerificationToken };

    // generated randomId to track the specific cts for a given request/promise.
    let randomId: string = this.generateRandomId();
    // ensure that the randomId is unique
    while (this.cancellationTokenMap.has(randomId)) {
      randomId = this.generateRandomId();
    }

    const cts: CancelTokenSource = this.getCancelTokenSource();
    // add a map to cancelTokenMap for randomId -> cts
    this.cancellationTokenMap.set(randomId, cts);

    config.cancelToken = cts.token;
    return {
      axiosRequestConfig: config,
      cancelTokenId: randomId
    };
  }

  private getCancelTokenSource(): CancelTokenSource {
    return axios.CancelToken.source();
  }

  private generateRandomId(): string {
    return Math.random()
      .toString(36)
      .substring(2) + new Date().getTime().toString(36);
  }

  private createResponseInterceptors(): any { }

  private getRetryClientCopy = (retries: number) => {
    const clientCopy = axios.create(this.client.defaults);
    axiosRetry(clientCopy, { retries: retries, retryCondition: (_error) => true });
    return clientCopy;
  }

  private _requestInterceptor = (innerConfig: AxiosRequestConfig) => {
    if(!this.emsSessionId)
    {
      // session id isn't set, so do nothing
      return innerConfig;
    }

    let shouldAddHeader = true;
    const isAbsoluteUrl = !!innerConfig.url && innerConfig.url.substring(0, 4) == 'http';
    if(isAbsoluteUrl) {
      const url = new URL(innerConfig.url);
      shouldAddHeader = includedHostnames.filter((h) => h == url.host).length > 0;
    }

    if(shouldAddHeader) {
        innerConfig.headers[emsSessionIdHeaderKey] = this.emsSessionId;
    }

    return innerConfig;
  };

  private _responseInterceptor = (response: AxiosResponse<any>) => {
    if(!this.emsSessionId && response.headers[emsSessionIdHeaderKey])
    {
      // session id isn't set, but is in the response from the server.  However,
      // we can't push it to redux since we can't import the redux store.  For
      // now, update our local value with the incoming one.
      // TODO - figure out how to push this session id back to redux w/o a circular dependency
      this.emsSessionId = response.headers[emsSessionIdHeaderKey];
    }

    return response;
  };
}

export const httpUtils = new HttpUtils();
